import React, { useState, useMemo } from "react";
import EventDetailsInfo from "./EventDetailsInfo";
import UpdateSubscribersAttendEvent from "./UpdateSubscribersAttendEvent";
import { useParams } from "react-router-dom";
import {
  useDeleteAttendee,
  useGetEventAttendees,
} from "../../hooks/useAttendee";
import { useCity } from "../../hooks/useCity";
import AddNewAttendeeAttendEvent from "./AddNewAttendeeAttendEvent";
import DataTable from "../shared/DataTable";
import { ToastContainer } from "react-toastify";
import AttendeesReportAttendEvent from "./AttendeesReportAttendEvent";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { usePermission } from "../../hooks/usePermission";
import { EventStatusConstants } from "../../constants/eventStatus";
import { useEventType } from "../../hooks/useEventType";
import { useGetEventDataById } from "../../hooks/useEvent";
import { useCurrentUser } from "../../hooks/useAuth";
import {
  ViewAttendeesPermission,
  EditAttendeesPermission,
  DeleteAttendeesPermission,
  ImportAttendeesPermission,
  ExportLOAPermission,
  CreateAttendeesPermission,
} from "../../constants/permissions";
import ImportAttendees from "./ImportAttendees";
import { useRoleNameById } from "../../hooks/useRole";
import RefreshAttendeesFromMasterData from "./RefreshAttendeesFromMasterData";
import { COMMERCIAL_SPONSORSHIP_EVENT_TYPE } from "../../constants/eventTypes";
import { ADMIN, SUPER_ADMIN } from "../../constants/roles";

const CityNameCell = ({ cityId, citiesMap }) => {
  return citiesMap?.get(cityId) || "";
};

export default function EventDetails() {
  const user = useCurrentUser();
  const { eventId } = useParams();
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [attendeeToDelete, setAttendeeToDelete] = useState(null);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const { data: eventData, isLoading: eventLoading } =
    useGetEventDataById(eventId);
  const { data: EventAttendees, isLoading: attendeesLoading } =
    useGetEventAttendees(eventId);
  const { data: citiesData } = useCity();
  const { data: eventTypeData } = useEventType();
  const { mutate: deleteAttendee } = useDeleteAttendee();
  const { data: userRoleName } = useRoleNameById(user?.role_id);

  const citiesMap = useMemo(() => {
    if (!citiesData) return new Map();
    return new Map(citiesData.map((city) => [city.id, city.name]));
  }, [citiesData]);

  const eventType = useMemo(
    () => eventTypeData?.find((type) => type.id === eventData?.event_type_id),
    [eventTypeData, eventData?.event_type_id]
  );

  const isCreator = useMemo(
    () => eventData?.created_by === user?.uid,
    [eventData?.created_by, user?.uid]
  );

  const isAdmin = useMemo(
    () => userRoleName === ADMIN || userRoleName === SUPER_ADMIN,
    [userRoleName]
  );
  const { data: canExportLOA } = usePermission({
    permissionName: ExportLOAPermission,
  });

  const { data: canImportAttendees } = usePermission({
    permissionName: ImportAttendeesPermission,
  });

  const { data: canCreateAttendees } = usePermission({
    permissionName: CreateAttendeesPermission,
  });

  const { data: canDeleteAttendees } = usePermission({
    permissionName: DeleteAttendeesPermission,
  });

  const { data: canUpdateAttendees } = usePermission({
    permissionName: EditAttendeesPermission,
  });

  const { data: canViewAttendees } = usePermission({
    permissionName: ViewAttendeesPermission,
  });

  const handleEditClick = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setOpenEditModal(true);
  };
  const handleDelete = (subscriber) => {
    setAttendeeToDelete(subscriber);
    setDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (attendeeToDelete) {
      deleteAttendee(attendeeToDelete.id);
      setDeleteModal(false);
      setAttendeeToDelete(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "National ID",
        selector: (row) => row.national_id,
        sortable: true,
        width: "130px",
      },
      {
        name: "Name",
        selector: (row) => `${row.first_name} ${row.family_name}`,
        sortable: true,
        width: "190px",
      },
      {
        name: "Email",
        selector: (row) => row.email,
        sortable: true,
        width: "220px",
      },
      {
        name: "Phone Number",
        selector: (row) => row.phone_number,
        sortable: true,
        width: "150px",
      },
      {
        name: "Specialty",
        selector: (row) => row.specialty,
        sortable: true,
        width: "120px",
      },
      {
        name: "Organization",
        selector: (row) => row.organization,
        sortable: true,
      },
      {
        name: "Industry Area",
        selector: (row) => row.industry_area,
        sortable: true,
        width: "160px",
      },
      {
        name: "City",
        selector: (row) => {
          return row.city_id ? (
            <CityNameCell cityId={row.city_id} citiesMap={citiesMap} />
          ) : (
            <span>N/A</span>
          );
        },
        sortable: true,
        width: "80px",
      },

      {
        name: "APO number",
        selector: (row) => row.APO_number,
        sortable: true,
        width: "140px",
      },
      {
        name: "HCP",
        selector: (row) => (row.APO_number ? "Yes" : "No"),
        sortable: true,
        width: "80px",
        cell: (row) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${
              !row.email?.endsWith("@abbott.com")
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {!row.email?.endsWith("@abbott.com") ? "Yes" : "No"}
          </span>
        ),
      },
      // {
      //   name: 'HCP',
      //   selector: row => row.attendee_type === 'guest' || row.attendee_type === 'user' ? 'No' : 'Yes',
      //   sortable: true,
      //   width: '80px',
      //   cell: row => (
      //     <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.attendee_type === 'guest' || row.attendee_type === 'user'
      //       ? 'bg-red-100 text-red-800'
      //       : 'bg-green-100 text-green-800'
      //       }`}>
      //       {row.attendee_type === 'guest' || row.attendee_type === 'user'? 'No' : 'Yes'}
      //     </span>
      //   ),
      // },
      {
        name: "Signature",
        selector: (row) => row.signature,
        sortable: true,
        width: "120px",
        cell: (row) =>
          row.signature &&
          row.signature !== "No Signature" &&
          row.signature !== "" ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(row.signature, "_blank");
              }}
              className="text-blueColor hover:text-blue-700 font-medium underline"
            >
              View Signature
            </button>
          ) : (
            <span className="text-blueColor">No Signature</span>
          ),
      },
      {
        name: "Actions",
        width: "100px",
        cell: (row) => {
          const eventType = eventTypeData?.find(
            (type) => type.id === eventData?.event_type_id
          );

          return (
            <div className="flex gap-2">
              {canUpdateAttendees && eventType?.can_edit_attendees && (
                <button
                  className="text-green-700 hover:text-blue-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(row);
                  }}
                >
                  <i className="fas fa-edit"></i>
                </button>
              )}
              {canDeleteAttendees && eventType?.can_delete_attendees && (
                <button
                  className="text-red-600 hover:text-red-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(row);
                  }}
                >
                  <i className="fas fa-trash"></i>
                </button>
              )}
            </div>
          );
        },
        omit:
          eventTypeData?.find((type) => type.id === eventData?.event_type_id)
            ?.name === COMMERCIAL_SPONSORSHIP_EVENT_TYPE ||
          eventData?.status === EventStatusConstants.CLOSED.text,
      },
    ],
    [
      eventTypeData,
      eventData,
      canUpdateAttendees,
      canDeleteAttendees,
      citiesMap,
    ]
  );

  return (
    <div className="container mx-auto py-2">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <EventDetailsInfo
        eventData={eventData}
        isLoading={eventLoading || attendeesLoading}
      />
      {canViewAttendees && (
        <>
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Attendees</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center flex-wrap mb-4 gap-4">
              <div className="flex items-center flex-wrap gap-4">
                {canExportLOA && (
                  <AttendeesReportAttendEvent eventId={eventId} />
                )}
                {eventData?.status !== EventStatusConstants.CLOSED.text &&
                  canImportAttendees &&
                  (() => {
                    if (!eventType || !eventType.can_add_attendees)
                      return false;
                    if (eventType.name === "Abbott-Organized Programs") {
                      return isCreator || isAdmin;
                    }
                    return true;
                  })() && (
                    <>
                      <ImportAttendees eventId={eventId} />
                      <RefreshAttendeesFromMasterData eventId={eventId} />
                    </>
                  )}
              </div>
              <div>
                {canCreateAttendees &&
                  eventData?.status !== EventStatusConstants.CLOSED.text &&
                  (() => {
                    if (!eventType || !eventType.can_add_attendees)
                      return false;
                    if (eventType.name === "Abbott-Organized Programs") {
                      return isCreator || isAdmin;
                    }
                    return true;
                  })() && (
                    <>
                      <button
                        className="rounded-md px-4 py-2 bg-purpleColor/90 text-white hover:bg-purpleColor transition-colors duration-200"
                        onClick={() => setOpenModal(true)}
                      >
                        <i className="fa-solid fa-plus text-white mr-2"></i>
                        Add New
                      </button>
                      <AddNewAttendeeAttendEvent
                        open={openModal}
                        onClose={() => setOpenModal(false)}
                        eventId={eventId}
                      />
                    </>
                  )}
              </div>
            </div>

            <DataTable
              columns={columns}
              data={EventAttendees || []}
              searchable={true}
              searchPlaceholder="Search attendees..."
              searchFields={[
                "first_name",
                "email",
                "phone_number",
                "organization",
                "specialty",
                "national_id",
              ]}
              paginationPerPage={10}
              customStyles={{
                cells: {
                  style: {
                    fontSize: "14px",
                    color: "#1e40af",
                    fontWeight: "500",
                  },
                },
              }}
            />

            <UpdateSubscribersAttendEvent
              openEditModal={openEditModal}
              setOpenEditModal={setOpenEditModal}
              selectedSubscriber={selectedSubscriber}
            />

            <Dialog
              open={deleteModal}
              onClose={() => setDeleteModal(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle
                id="alert-dialog-title"
                className="bg-red-50 text-red-700"
              >
                Confirm Deletion
              </DialogTitle>
              <DialogContent className="mt-10 mb-4">
                <DialogContentText id="alert-dialog-description">
                  Are you sure you want to delete {attendeeToDelete?.first_name}
                  ?
                </DialogContentText>
              </DialogContent>
              <DialogActions className="px-4">
                <button
                  onClick={() => setDeleteModal(false)}
                  className="px-4 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:rounded"
                >
                  Cancel
                </button>
                <button
                  data-track="delete-attendee-button"
                  data-module="Attendees"
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </DialogActions>
            </Dialog>
          </div>
        </>
      )}
    </div>
  );
}
